/**
 * pages/TodoPage.tsx
 * Route: /tasks
 * Full todo manager — create, complete, reschedule, search, notifications
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Check, Trash2, Clock, AlertTriangle, ChevronDown, ChevronUp, Bell, BellOff, Calendar, RotateCcw, Search, X, CheckCircle2, Circle, Loader } from 'lucide-react';
import { useStore } from '../store';
import { useAuth } from '../hooks';
import { toast } from '../components/ui/Toast';
import { getToken } from '../api/client';

const C = {
  amber100:'#FFF4E0', amber200:'#FFD88A', amber300:'#F5A623', amber400:'#C4811A', amber500:'#8B5A0A',
  ink:'#1A1814', inkLight:'#3D3A34', muted:'#7A7570', border:'#E8E4DC', surface:'#FAFAF8', white:'#FFFFFF',
  red:'#DC2626', red100:'#FEF2F2', green:'#16A34A', green100:'#F0FDF4',
  blue:'#2563EB', blue100:'#EFF6FF',
};
const font = { head:"'Sora',sans-serif", body:"'DM Sans',sans-serif" };

// ── Types ─────────────────────────────────────────────────────────────────────
interface Todo {
  id: string;
  title: string;
  description?: string;
  dueDate?: string;
  priority: 'high' | 'medium' | 'low';
  completed: boolean;
  completedAt?: string;
  snoozedTo?: string;
  isOverdue: boolean;
  createdAt: string;
}

// ── API calls ─────────────────────────────────────────────────────────────────
const API = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
// getToken imported from client.ts
const headers = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` });

const todosApi = {
  list:     ()                    => fetch(`${API}/todos`,         { headers: headers() }).then(r => r.json()),
  create:   (body: any)           => fetch(`${API}/todos`,         { method:'POST',   headers: headers(), body: JSON.stringify(body) }).then(r => r.json()),
  update:   (id: string, body: any) => fetch(`${API}/todos/${id}`, { method:'PATCH',  headers: headers(), body: JSON.stringify(body) }).then(r => r.json()),
  delete:   (id: string)          => fetch(`${API}/todos/${id}`,   { method:'DELETE', headers: headers() }),
};

// ── Priority config ───────────────────────────────────────────────────────────
const PRIORITY = {
  high:   { label: 'High',   color: C.red,    bg: C.red100,    icon: '🔴' },
  medium: { label: 'Medium', color: C.amber400, bg: C.amber100, icon: '🟡' },
  low:    { label: 'Low',    color: C.green,  bg: C.green100,  icon: '🟢' },
};

// ── Notification helper ───────────────────────────────────────────────────────
const scheduleNotification = (todo: Todo) => {
  if (!todo.dueDate || todo.completed) return;
  if (!('Notification' in window)) return;
  const due = new Date(todo.dueDate).getTime();
  const now  = Date.now();
  const diff = due - now;
  if (diff <= 0) return;
  if (diff > 24 * 60 * 60 * 1000) return; // only schedule if within 24h
  setTimeout(() => {
    if (Notification.permission === 'granted') {
      new Notification(`⏰ Task due: ${todo.title}`, {
        body: todo.description || 'Your task is due now.',
        icon: '/logo192.png',
        badge: '/logo192.png',
        tag: todo.id,
      });
    }
  }, diff);
};

// ── Format due date ───────────────────────────────────────────────────────────
const formatDue = (dateStr: string) => {
  const d = new Date(dateStr);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const isTomorrow = d.toDateString() === new Date(now.getTime() + 86400000).toDateString();
  const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  if (isToday)    return `Today, ${time}`;
  if (isTomorrow) return `Tomorrow, ${time}`;
  return d.toLocaleDateString('en-GB', { day:'numeric', month:'short' }) + `, ${time}`;
};

// ── TodoPage ──────────────────────────────────────────────────────────────────
const TodoPage: React.FC = () => {
  const { isAuthenticated, authLoading } = useAuth();
  const initAuth = useStore(s => s.initAuth);

  const [todos,       setTodos]       = useState<Todo[]>([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState('');
  const [showDone,    setShowDone]    = useState(false);
  const [notifPerms,  setNotifPerms]  = useState(Notification.permission);
  const [showForm,    setShowForm]    = useState(false);
  const [reschedule,  setReschedule]  = useState<string | null>(null);

  // New todo form state
  const [newTitle,    setNewTitle]    = useState('');
  const [newDesc,     setNewDesc]     = useState('');
  const [newDue,      setNewDue]      = useState('');
  const [newPriority, setNewPriority] = useState<'high'|'medium'|'low'>('medium');
  const [saving,      setSaving]      = useState(false);
  const [newRescDate, setNewRescDate] = useState('');

  useEffect(() => { initAuth(); }, []); // eslint-disable-line

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await todosApi.list();
      if (Array.isArray(data)) {
        const valid = data.filter(t => t && t.id && t.title);
        setTodos(valid);
        valid.forEach(scheduleNotification);
      }
    } catch { toast.error('Could not load tasks'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (isAuthenticated) load(); }, [isAuthenticated, load]);

  // Request notification permission
  const requestNotifs = async () => {
    const perm = await Notification.requestPermission();
    setNotifPerms(perm);
    if (perm === 'granted') toast.success('Notifications enabled!');
    else toast.error('Notifications blocked. Enable in browser settings.');
  };

  // Create todo
  const createTodo = async () => {
    if (!newTitle.trim()) return;
    setSaving(true);
    try {
      const todo = await todosApi.create({
        title: newTitle.trim(),
        description: newDesc.trim() || undefined,
        dueDate: newDue || undefined,
        priority: newPriority,
      });
      // Ensure todo has required fields before adding to state
      if (!todo || !todo.id) { toast.error('Task created but response was unexpected — please refresh'); return; }
      if (!todo.title) todo.title = newTitle.trim();
      if (!todo.priority) todo.priority = newPriority;
      setTodos(prev => [todo, ...prev]);
      scheduleNotification(todo);
      setNewTitle(''); setNewDesc(''); setNewDue(''); setNewPriority('medium');
      setShowForm(false);
      toast.success('Task created!');
    } catch { toast.error('Could not create task'); }
    finally { setSaving(false); }
  };

  // Toggle complete
  const toggleComplete = async (todo: Todo) => {
    const updated = await todosApi.update(todo.id, { completed: !todo.completed });
    setTodos(prev => prev.map(t => t.id === todo.id ? updated : t));
    if (!todo.completed) toast.success('Task completed! 🎉');
  };

  // Reschedule
  const doReschedule = async (id: string) => {
    if (!newRescDate) return;
    const updated = await todosApi.update(id, { dueDate: newRescDate, completed: false });
    setTodos(prev => prev.map(t => t.id === id ? updated : t));
    scheduleNotification(updated);
    setReschedule(null); setNewRescDate('');
    toast.success('Task rescheduled!');
  };

  // Delete
  const deleteTodo = async (id: string) => {
    await todosApi.delete(id);
    setTodos(prev => prev.filter(t => t.id !== id));
    toast.success('Task deleted');
  };

  if (authLoading || loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh', background:C.surface, flexDirection:'column', gap:16 }}>
      <Loader size={32} color={C.amber300} style={{ animation:'spin .7s linear infinite' }}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <p style={{ fontSize:14, color:C.muted, fontFamily:font.body }}>Loading tasks…</p>
    </div>
  );

  // Filter
  const q = search.toLowerCase();
  const filtered = todos.filter(t =>
    !t.completed && ((t.title||'').toLowerCase().includes(q) || (t.description || '').toLowerCase().includes(q))
  );
  const overdue   = filtered.filter(t => t.isOverdue);
  const upcoming  = filtered.filter(t => !t.isOverdue);
  const completed = todos.filter(t => t.completed && ((t.title||'').toLowerCase().includes(q) || (t.description||'').toLowerCase().includes(q)));

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700;800&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
        body{font-family:'DM Sans',sans-serif;background:${C.surface};}
        .todo-input{width:100%;padding:10px 14px;border-radius:10px;border:1.5px solid ${C.border};font-size:14px;font-family:'DM Sans',sans-serif;color:${C.ink};outline:none;background:${C.white};transition:border-color .15s;}
        .todo-input:focus{border-color:${C.amber300};box-shadow:0 0 0 3px ${C.amber300}22;}
        .todo-input::placeholder{color:${C.muted};}
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
      `}</style>

      <div style={{ minHeight:'100vh', background:C.surface }}>

        {/* Top bar */}
        <div style={{ background:C.white, borderBottom:`1px solid ${C.border}`, padding:'0 clamp(16px,3vw,28px)', height:60, display:'flex', alignItems:'center', gap:14, position:'sticky', top:0, zIndex:40 }}>
          <a href="/dashboard" style={{ display:'flex', alignItems:'center', gap:6, textDecoration:'none' }}>
            <div style={{ width:30, height:30, borderRadius:9, background:`linear-gradient(145deg,${C.amber200},${C.amber400})`, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <svg width="13" height="13" viewBox="0 0 180 180" fill="none">
                <g transform="translate(90,88) rotate(-35)"><rect x="-12" y="-38" width="24" height="58" rx="7" fill="white"/><rect x="-12" y="-38" width="24" height="16" rx="7" fill="#F08030" opacity="0.9"/><polygon points="-12,20 12,20 0,44" fill="white" opacity="0.9"/></g>
                <circle cx="104" cy="102" r="42" fill="none" stroke="white" strokeWidth="13" opacity="0.95"/>
                <g transform="translate(126,124) rotate(45)"><rect x="-6" y="-4" width="30" height="10" rx="5" fill="white"/></g>
              </svg>
            </div>
            <span style={{ fontFamily:font.head, fontWeight:800, fontSize:18, letterSpacing:'-0.05em', color:C.ink }}>jötr</span>
          </a>

          <div style={{ flex:1, maxWidth:400 }}>
            <div style={{ position:'relative' }}>
              <Search size={14} color={C.muted} style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)' }}/>
              <input className="todo-input" value={search} onChange={e=>setSearch(e.target.value)}
                placeholder="Search tasks…" style={{ paddingLeft:34, height:38 }}/>
              {search && <button onClick={()=>setSearch('')} style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:C.muted, display:'flex' }}><X size={13}/></button>}
            </div>
          </div>

          <div style={{ display:'flex', alignItems:'center', gap:8, marginLeft:'auto' }}>
            {/* Notification toggle */}
            <button onClick={requestNotifs} title={notifPerms === 'granted' ? 'Notifications on' : 'Enable notifications'}
              style={{ background:notifPerms==='granted'?C.amber100:'none', border:`1.5px solid ${notifPerms==='granted'?C.amber300:C.border}`, borderRadius:9, padding:'6px 10px', cursor:'pointer', display:'flex', alignItems:'center', gap:5, fontSize:12, fontFamily:font.body, fontWeight:600, color:notifPerms==='granted'?C.amber500:C.muted }}>
              {notifPerms === 'granted' ? <Bell size={14}/> : <BellOff size={14}/>}
              <span style={{ display:'none', ['@media (min-width:480px)' as any]:{ display:'inline' } }}>
                {notifPerms === 'granted' ? 'Notifs on' : 'Enable notifs'}
              </span>
            </button>

            {/* New task button */}
            <button onClick={()=>setShowForm(s=>!s)}
              style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 16px', borderRadius:10, border:'none', background:`linear-gradient(135deg,${C.amber300},${C.amber400})`, color:C.white, fontWeight:700, fontSize:14, fontFamily:font.body, cursor:'pointer', boxShadow:`0 4px 12px ${C.amber300}44` }}>
              <Plus size={15}/> New task
            </button>
          </div>
        </div>

        <div style={{ maxWidth:700, margin:'0 auto', padding:'28px clamp(16px,3vw,28px) 80px' }}>

          {/* Page title */}
          <div style={{ marginBottom:24 }}>
            <h1 style={{ fontFamily:font.head, fontWeight:800, fontSize:28, letterSpacing:'-0.04em', color:C.ink, marginBottom:4 }}>Tasks</h1>
            <p style={{ fontSize:13, color:C.muted, fontFamily:font.body }}>
              {upcoming.length} upcoming · {overdue.length} overdue · {completed.length} completed
            </p>
          </div>

          {/* Notification banner */}
          {notifPerms === 'default' && (
            <div style={{ background:C.amber100, border:`1px solid ${C.amber200}`, borderRadius:12, padding:'12px 16px', marginBottom:20, display:'flex', alignItems:'center', gap:12, animation:'fadeIn .3s ease' }}>
              <Bell size={16} color={C.amber500}/>
              <p style={{ fontSize:13, color:C.amber500, fontFamily:font.body, fontWeight:600, flex:1 }}>
                Enable notifications to get reminders when tasks are due.
              </p>
              <button onClick={requestNotifs} style={{ padding:'6px 14px', borderRadius:8, border:'none', background:C.amber300, color:C.white, fontWeight:700, fontSize:12, fontFamily:font.body, cursor:'pointer' }}>
                Enable
              </button>
            </div>
          )}

          {/* Create form */}
          {showForm && (
            <div style={{ background:C.white, border:`1.5px solid ${C.amber300}`, borderRadius:16, padding:'20px 20px 16px', marginBottom:24, boxShadow:`0 4px 20px ${C.amber300}22`, animation:'fadeIn .25s ease' }}>
              <h3 style={{ fontFamily:font.head, fontWeight:700, fontSize:16, color:C.ink, marginBottom:16 }}>New task</h3>
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                <input className="todo-input" value={newTitle} onChange={e=>setNewTitle(e.target.value)}
                  placeholder="Task title…" onKeyDown={e=>e.key==='Enter'&&createTodo()} autoFocus/>
                <textarea className="todo-input" value={newDesc} onChange={e=>setNewDesc(e.target.value)}
                  placeholder="Description (optional)…" rows={2} style={{ resize:'vertical', padding:'10px 14px' }}/>
                <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                  <div style={{ flex:1, minWidth:180 }}>
                    <label style={{ fontSize:11, fontWeight:600, color:C.muted, fontFamily:font.body, display:'block', marginBottom:4 }}>Due date & time</label>
                    <input className="todo-input" type="datetime-local" value={newDue} onChange={e=>setNewDue(e.target.value)}
                      min={new Date().toISOString().slice(0,16)}/>
                  </div>
                  <div style={{ flex:1, minWidth:140 }}>
                    <label style={{ fontSize:11, fontWeight:600, color:C.muted, fontFamily:font.body, display:'block', marginBottom:4 }}>Priority</label>
                    <select className="todo-input" value={newPriority} onChange={e=>setNewPriority(e.target.value as any)}>
                      <option value="high">🔴 High</option>
                      <option value="medium">🟡 Medium</option>
                      <option value="low">🟢 Low</option>
                    </select>
                  </div>
                </div>
                <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
                  <button onClick={()=>setShowForm(false)} style={{ padding:'9px 18px', borderRadius:9, border:`1.5px solid ${C.border}`, background:C.surface, cursor:'pointer', fontFamily:font.body, fontWeight:600, fontSize:13, color:C.inkLight }}>
                    Cancel
                  </button>
                  <button onClick={createTodo} disabled={saving || !newTitle.trim()}
                    style={{ padding:'9px 20px', borderRadius:9, border:'none', background:newTitle.trim()?`linear-gradient(135deg,${C.amber300},${C.amber400})`:'#ccc', cursor:newTitle.trim()?'pointer':'not-allowed', fontFamily:font.body, fontWeight:700, fontSize:13, color:C.white, display:'flex', alignItems:'center', gap:6 }}>
                    {saving ? <Loader size={13} style={{ animation:'spin .7s linear infinite' }}/> : <Plus size={13}/>}
                    {saving ? 'Creating…' : 'Create task'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Overdue tasks */}
          {overdue.length > 0 && (
            <Section title={`Overdue (${overdue.length})`} color={C.red} icon={<AlertTriangle size={15} color={C.red}/>}>
              {overdue.map(todo => (
                <TodoItem key={todo.id} todo={todo}
                  onToggle={toggleComplete} onDelete={deleteTodo}
                  onReschedule={()=>setReschedule(todo.id)}
                  isRescheduling={reschedule === todo.id}
                  newRescDate={newRescDate} setNewRescDate={setNewRescDate}
                  doReschedule={()=>doReschedule(todo.id)}
                  cancelReschedule={()=>setReschedule(null)}/>
              ))}
            </Section>
          )}

          {/* Upcoming tasks */}
          {upcoming.length > 0 ? (
            <Section title={`Upcoming (${upcoming.length})`} color={C.amber400} icon={<Clock size={15} color={C.amber400}/>}>
              {upcoming.map(todo => (
                <TodoItem key={todo.id} todo={todo}
                  onToggle={toggleComplete} onDelete={deleteTodo}
                  onReschedule={()=>setReschedule(todo.id)}
                  isRescheduling={reschedule === todo.id}
                  newRescDate={newRescDate} setNewRescDate={setNewRescDate}
                  doReschedule={()=>doReschedule(todo.id)}
                  cancelReschedule={()=>setReschedule(null)}/>
              ))}
            </Section>
          ) : !loading && overdue.length === 0 && (
            <div style={{ textAlign:'center', padding:'60px 20px', color:C.muted }}>
              <CheckCircle2 size={48} color={C.border} style={{ marginBottom:16 }}/>
              <p style={{ fontFamily:font.head, fontWeight:700, fontSize:18, color:C.ink, marginBottom:8 }}>
                {search ? 'No tasks found' : 'No tasks yet'}
              </p>
              <p style={{ fontSize:13, fontFamily:font.body, marginBottom:20 }}>
                {search ? 'Try a different search term' : 'Create your first task to get started.'}
              </p>
              {!search && (
                <button onClick={()=>setShowForm(true)} style={{ padding:'10px 22px', borderRadius:10, border:'none', background:`linear-gradient(135deg,${C.amber300},${C.amber400})`, color:C.white, fontWeight:700, fontSize:14, fontFamily:font.body, cursor:'pointer' }}>
                  <Plus size={14} style={{ marginRight:6, display:'inline' }}/>Create task
                </button>
              )}
            </div>
          )}

          {/* Completed tasks */}
          {completed.length > 0 && (
            <div style={{ marginTop:24 }}>
              <button onClick={()=>setShowDone(s=>!s)} style={{ display:'flex', alignItems:'center', gap:8, background:'none', border:'none', cursor:'pointer', padding:'8px 0', fontFamily:font.body, fontWeight:600, fontSize:13, color:C.muted }}>
                {showDone ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                Completed ({completed.length})
              </button>
              {showDone && (
                <div style={{ marginTop:8, opacity:0.7 }}>
                  {completed.map(todo => (
                    <TodoItem key={todo.id} todo={todo}
                      onToggle={toggleComplete} onDelete={deleteTodo}
                      onReschedule={()=>setReschedule(todo.id)}
                      isRescheduling={reschedule === todo.id}
                      newRescDate={newRescDate} setNewRescDate={setNewRescDate}
                      doReschedule={()=>doReschedule(todo.id)}
                      cancelReschedule={()=>setReschedule(null)}/>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// ── Section wrapper ───────────────────────────────────────────────────────────
const Section: React.FC<{ title:string; color:string; icon:React.ReactNode; children:React.ReactNode }> = ({ title, color, icon, children }) => (
  <div style={{ marginBottom:24 }}>
    <div style={{ display:'flex', alignItems:'center', gap:7, marginBottom:12 }}>
      {icon}
      <span style={{ fontSize:12, fontWeight:700, color, fontFamily:font.body, textTransform:'uppercase', letterSpacing:'0.06em' }}>{title}</span>
    </div>
    <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
      {children}
    </div>
  </div>
);

// ── Todo item ─────────────────────────────────────────────────────────────────
const TodoItem: React.FC<{
  todo: Todo;
  onToggle: (t:Todo)=>void;
  onDelete: (id:string)=>void;
  onReschedule: ()=>void;
  isRescheduling: boolean;
  newRescDate: string;
  setNewRescDate: (v:string)=>void;
  doReschedule: ()=>void;
  cancelReschedule: ()=>void;
}> = ({ todo, onToggle, onDelete, onReschedule, isRescheduling, newRescDate, setNewRescDate, doReschedule, cancelReschedule }) => {
  const p = PRIORITY[todo.priority];

  return (
    <div style={{ background:C.white, border:`1px solid ${todo.isOverdue?C.red+'44':todo.completed?C.border:C.border}`, borderRadius:14, padding:'14px 16px', animation:'fadeIn .2s ease', transition:'box-shadow .15s' }}
      onMouseEnter={e=>(e.currentTarget as HTMLDivElement).style.boxShadow='0 4px 16px rgba(0,0,0,0.07)'}
      onMouseLeave={e=>(e.currentTarget as HTMLDivElement).style.boxShadow='none'}>

      <div style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
        {/* Checkbox */}
        <button onClick={()=>onToggle(todo)} style={{ background:'none', border:'none', cursor:'pointer', padding:0, marginTop:1, flexShrink:0, color:todo.completed?C.green:C.border, display:'flex' }}>
          {todo.completed ? <CheckCircle2 size={20} color={C.green}/> : <Circle size={20} color={C.muted}/>}
        </button>

        {/* Content */}
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:todo.description?4:0 }}>
            <span style={{ fontSize:14, fontWeight:600, color:todo.completed?C.muted:C.ink, fontFamily:font.body, textDecoration:todo.completed?'line-through':'none', wordBreak:'break-word' }}>
              {todo.title}
            </span>
            <span style={{ fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:20, background:p.bg, color:p.color, fontFamily:font.body, flexShrink:0 }}>
              {p.icon} {p.label}
            </span>
          </div>

          {todo.description && (
            <p style={{ fontSize:12, color:C.muted, fontFamily:font.body, lineHeight:1.6, marginBottom:6 }}>{todo.description}</p>
          )}

          {todo.dueDate && (
            <div style={{ display:'flex', alignItems:'center', gap:4 }}>
              <Clock size={11} color={todo.isOverdue?C.red:C.muted}/>
              <span style={{ fontSize:11, color:todo.isOverdue?C.red:C.muted, fontFamily:font.body, fontWeight:todo.isOverdue?700:400 }}>
                {todo.isOverdue ? '⚠ Overdue — ' : ''}{formatDue(todo.dueDate)}
              </span>
            </div>
          )}

          {todo.completedAt && (
            <div style={{ display:'flex', alignItems:'center', gap:4, marginTop:2 }}>
              <Check size={11} color={C.green}/>
              <span style={{ fontSize:11, color:C.green, fontFamily:font.body }}>
                Completed {formatDue(todo.completedAt)}
              </span>
            </div>
          )}

          {/* Reschedule form */}
          {isRescheduling && (
            <div style={{ marginTop:10, padding:'10px 12px', background:C.amber100, borderRadius:10, display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
              <Calendar size={13} color={C.amber500}/>
              <input type="datetime-local" value={newRescDate} onChange={e=>setNewRescDate(e.target.value)}
                min={new Date().toISOString().slice(0,16)}
                style={{ flex:1, minWidth:160, padding:'6px 10px', borderRadius:8, border:`1.5px solid ${C.amber300}`, fontFamily:font.body, fontSize:12, outline:'none', background:C.white }}/>
              <button onClick={doReschedule} disabled={!newRescDate}
                style={{ padding:'6px 12px', borderRadius:8, border:'none', background:newRescDate?`linear-gradient(135deg,${C.amber300},${C.amber400})`:'#ccc', color:C.white, fontWeight:700, fontSize:12, fontFamily:font.body, cursor:newRescDate?'pointer':'not-allowed' }}>
                Save
              </button>
              <button onClick={cancelReschedule} style={{ padding:'6px 10px', borderRadius:8, border:`1.5px solid ${C.border}`, background:C.white, color:C.muted, fontWeight:600, fontSize:12, fontFamily:font.body, cursor:'pointer' }}>
                Cancel
              </button>
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={{ display:'flex', gap:4, flexShrink:0 }}>
          {(todo.isOverdue || !todo.completed) && (
            <button onClick={onReschedule} title="Reschedule"
              style={{ background:'none', border:'none', cursor:'pointer', color:C.muted, padding:5, borderRadius:6, display:'flex' }}
              onMouseEnter={e=>(e.currentTarget as HTMLButtonElement).style.background=C.amber100}
              onMouseLeave={e=>(e.currentTarget as HTMLButtonElement).style.background='none'}>
              <RotateCcw size={14}/>
            </button>
          )}
          <button onClick={()=>onDelete(todo.id)} title="Delete"
            style={{ background:'none', border:'none', cursor:'pointer', color:C.muted, padding:5, borderRadius:6, display:'flex' }}
            onMouseEnter={e=>{(e.currentTarget as HTMLButtonElement).style.background=C.red100;(e.currentTarget as HTMLButtonElement).style.color=C.red;}}
            onMouseLeave={e=>{(e.currentTarget as HTMLButtonElement).style.background='none';(e.currentTarget as HTMLButtonElement).style.color=C.muted;}}>
            <Trash2 size={14}/>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TodoPage;